import axios from 'axios';
import apiClient from '@/api/apiClient';
import type {
  Article,
  ArticlePage,
  ArticleQuery,
  CreateArticleInput,
  UpdateArticleInput,
} from '../types';

interface ApiResponse<T> {
  code: number | string;
  message: string;
  result: T;
}

interface ApiErrorBody {
  code?: number | string;
  message?: string;
  result?: Record<string, string[]>;
}

export class ArticlesApiError extends Error {
  code?: number | string;
  status?: number;
  fieldErrors?: Record<string, string[]>;

  constructor(
    message: string,
    options: { code?: number | string; status?: number; fieldErrors?: Record<string, string[]> } = {},
  ) {
    super(message);
    this.name = 'ArticlesApiError';
    this.code = options.code;
    this.status = options.status;
    this.fieldErrors = options.fieldErrors;
  }
}

export const toArticlesApiError = (error: unknown): ArticlesApiError => {
  if (error instanceof ArticlesApiError) return error;
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    return new ArticlesApiError(
      error.response?.data?.message
        || (error.code === 'ECONNABORTED'
          ? 'Yêu cầu đã quá thời gian chờ. Vui lòng thử lại.'
          : error.response
            ? 'Máy chủ không thể thực hiện yêu cầu.'
            : 'Không thể kết nối đến máy chủ.'),
      {
        code: error.response?.data?.code,
        status: error.response?.status,
        fieldErrors: error.response?.data?.result,
      },
    );
  }
  return new ArticlesApiError(
    error instanceof Error ? error.message : 'Đã xảy ra lỗi không xác định.',
  );
};

const withNormalizedError = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request();
  } catch (error: unknown) {
    throw toArticlesApiError(error);
  }
};

export const getArticles = (query: ArticleQuery): Promise<ArticlePage> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<ArticlePage>>('/api/articles', { params: query });
    return response.data.result;
  });

export const getArticleById = (articleId: number): Promise<Article> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<Article>>(`/api/articles/${articleId}`);
    return response.data.result;
  });

export const createArticle = (input: CreateArticleInput): Promise<Article> =>
  withNormalizedError(async () => {
    const response = await apiClient.post<ApiResponse<Article>>('/api/articles', input);
    return response.data.result;
  });

export const updateArticle = (articleId: number, input: UpdateArticleInput): Promise<Article> =>
  withNormalizedError(async () => {
    const response = await apiClient.put<ApiResponse<Article>>(`/api/articles/${articleId}`, input);
    return response.data.result;
  });

export const deleteArticle = (articleId: number): Promise<void> =>
  withNormalizedError(async () => {
    await apiClient.delete(`/api/articles/${articleId}`);
  });
